<template>
    <div class="MainContactEditor">
        <div v-if="CheckPermission(ViewPermissionRequirement)">
            <b-container>
                <b-row>
                    <b-col cols="11">
                        <vuetable :fields="Fields"
                                  :data="FieldData"
                                  pagination-path=""
                                  :api-mode="false"
                                  class="vue-datatable"
                                  :row-class="onRowClass">

                            <template slot="RecordActions" scope="props">
                                <div class="RecordActions">
                                    <div class="horz-icons" v-if="EditMode">
                                        <font-awesome-icon :icon="['fa', 'edit']" class="pointer" @click="ShowEditRecord(props.rowData)" />
                                        <span>
                                            <DeleteIcon :requires-confirmation="true"
                                                        confirmation-string="WARNING: You are about to delete this Main Contact.  This should only be done if they were added my mistake.  If they were once (but no longer) associated with this church, edit them and set an end date instead."
                                                        endpoint="ChurchEngagement/People/DeleteChurchContact"
                                                        :payload="{Id: props.rowData.Id}"
                                                        :perform-global-update="true"
                                                        button-variant="danger"
                                                        button-size="md" />
                                        </span>
                                    </div>
                                </div>
                            </template>
                            <template slot="Email" scope="props">
                                <div class="Email">
                                    <a :href="'mailto:' + props.rowData.Volunteer.Person.Email">{{ props.rowData.Volunteer.Person.Email }}</a>
                                </div>
                            </template>
                        </vuetable>
                    </b-col>
                    <b-col cols="1">
                        <div v-if="EditMode && CheckPermission(EditPermissionRequirement)" class="vert-icons">
                            <b-row>
                                <font-awesome-icon :icon="['fa', 'plus']" v-on:click="ShowAddRecord()" class="pointer olive-green-text" />
                            </b-row>
                        </div>
                    </b-col>
                </b-row>
            </b-container>

            <!--ADD NEW RECORD MODAL-->
            <b-modal ref="add-modal" title="Add New Main Contact" hide-footer>
                <b-container>
                    <b-form @submit="AddRecord">
                        <b-row>
                            <b-col>
                                <div class="form-group">
                                    <label>First Name:</label>
                                    <input type="text" class="form-control" v-model="NewRecord.FirstName" required />
                                </div>
                                <div class="form-group">
                                    <label>Last Name:</label>
                                    <input type="text" class="form-control" v-model="NewRecord.LastName" required />
                                </div>
                            </b-col>
                            <b-col>
                                <div class="form-group">
                                    <label>Phone Number:</label>
                                    <input type="text" class="form-control" v-model="NewRecord.PhoneNumber" />
                                </div>
                            </b-col>
                        </b-row>
                        <b-row>
                            <b-col>
                                <div class="form-group">
                                    <label>Email:</label>
                                    <input type="text" class="form-control" v-model="NewRecord.Email"  />
                                </div>
                            </b-col>
                        </b-row>
                        <b-row>
                            <b-col>
                                <div class="form-group">
                                    <label>Start Date:</label>
                                    <flat-pickr v-model="NewRecord.StartDate" class="form-control" ref="showfocus" required :config="DateConfig"></flat-pickr>
                                </div>
                            </b-col>
                            <b-col>
                                <div class="form-group">
                                    <label>End Date:</label>
                                    <flat-pickr v-model="NewRecord.EndDate" class="form-control" ref="showfocus" :config="DateConfig"></flat-pickr>
                                </div>
                            </b-col>
                        </b-row>
                        <b-row class="modal-btn-row">
                            <div class="modal-btn-row-div">
                                <b-button size="md" variant="secondary" @click="CancelAddRecord()">
                                    CANCEL
                                </b-button>
                                <b-button size="md" variant="success" type="submit">
                                    ADD
                                </b-button>
                            </div>
                        </b-row>
                    </b-form>
                </b-container>
            </b-modal>

            <!--EDIT RECORD MODAL-->
            <b-modal ref="edit-modal" title="Edit Main Contact" hide-footer>
                <b-container>
                    <b-form @submit="UpdateRecord">
                        <b-row>
                            <b-col>
                                <div class="form-group">
                                    <label>First Name:</label>
                                    <input type="text" class="form-control" v-model="NewRecord.FirstName" required />
                                </div>
                                <div class="form-group">
                                    <label>Last Name:</label>
                                    <input type="text" class="form-control" v-model="NewRecord.LastName" required />
                                </div>
                            </b-col>
                            <b-col>
                                <div class="form-group">
                                    <label>Phone Number:</label>
                                    <input type="text" class="form-control" v-model="NewRecord.PhoneNumber" />
                                </div>
                            </b-col>
                        </b-row>
                        <b-row>
                            <b-col>
                                <div class="form-group">
                                    <label>Email:</label>
                                    <input type="text" class="form-control" v-model="NewRecord.Email" />
                                </div>
                            </b-col>
                        </b-row>
                        <b-row>
                            <b-col>
                                <div class="form-group">
                                    <label>Start Date:</label>
                                    <flat-pickr v-model="NewRecord.StartDate" class="form-control" ref="showfocus" required :config="DateConfig"></flat-pickr>
                                </div>
                            </b-col>
                            <b-col>
                                <div class="form-group">
                                    <label>End Date:</label>
                                    <flat-pickr v-model="NewRecord.EndDate" class="form-control" ref="showfocus" :config="DateConfig"></flat-pickr>
                                </div>
                            </b-col>
                        </b-row>
                        <b-row class="modal-btn-row">
                            <div class="modal-btn-row-div">
                                <b-button size="md" variant="secondary" @click="CancelEditRecord()">
                                    CANCEL
                                </b-button>
                                <b-button size="md" variant="success" type="submit">
                                    SAVE
                                </b-button>
                            </div>
                        </b-row>
                    </b-form>
                </b-container>
            </b-modal>
        </div>
        <div v-else>
            PRIVATE
        </div>
    </div>

</template>

<script>
    //import icons
    import { library } from '@fortawesome/fontawesome-svg-core';
    import { faPlus } from '@fortawesome/free-solid-svg-icons';
    library.add(faPlus);
    import { faEdit } from '@fortawesome/free-solid-svg-icons';
    library.add(faEdit);
    import { faTrashAlt } from '@fortawesome/free-solid-svg-icons';
    library.add(faTrashAlt);

    //import components
    import Vuetable from 'vuetable-2/src/components/Vuetable';
    import flatPickr from 'vue-flatpickr-component';
    import 'flatpickr/dist/flatpickr.css';

    import DeleteIcon from '../../../Core/ItemEditors/DeleteIcon.vue';
    import { EventBus } from '../../../../services/event-bus.js';

    export default {
        props: [
            "FieldData",
            "RecordId",
            "EditMode"
        ],
        data: function () {
            return {
                Fields: [
                    {
                        name: 'Volunteer.Person.FullName',
                        title: 'NAME',
                    },
                    //{
                    //    name: 'Volunteer.Person.PhoneNumbers',
                    //    title: 'Phone',
                    //    callback: 'GetPhoneNumberFromPhoneArray'
                    //},
                    //{
                    //    name: '__slot:Email',
                    //    title: 'Email',
                    //},
                    {
                        name: 'StartDate',
                        title: "START",
                        callback: 'formatDate|MM/DD/YY',
                    },
                    {
                        name: 'EndDate',
                        title: "END",
                        callback: 'formatDate|MM/DD/YY|N/A',
                    },
                    {
                        name: '__slot:RecordActions',
                        title: "ACTIONS",
                    }
                ],
                NewRecord: {
                    FirstName: "",
                    LastName: "",
                    Email: "",
                    PhoneNumber: "",
                    Id: "",
                    StartDate: "",
                    EndDate: ""
                },
                
                DateConfig: {
                    dateFormat: 'm/d/Y',
                    allowInput: true,
                    required: true
                }
            };
        },
        methods: {
            onRowClass(dataItem, index) {
                return (dataItem.EndDate == null) ? 'current-record' : '';
            },
            GetPhoneNumberFromPhoneArray(phoneArray) {
                return phoneArray[0].PhoneNumber.FormattedPhoneNumber;
            },
            ResetNewRecord() {
                this.NewRecord.FirstName = "";
                this.NewRecord.LastName = "";
                this.NewRecord.Email = "";
                this.NewRecord.PhoneNumber = "";
                this.NewRecord.StartDate = "";
                this.NewRecord.EndDate = "";
                this.NewRecord.Id = "";
            },
            //ADD RECORD
            ShowAddRecord() {
                this.ResetNewRecord();
                this.$refs['add-modal'].show();
            },
            AddRecord(e) {
                e.preventDefault();
                //var vm = this;
                let initialPayload = { ChurchID: this.RecordId };
                let finalPayload = { ...initialPayload, ...this.NewRecord };
                this.$MasterAPI.ExecuteRequest("POST", "ChurchEngagement/People/AddChurchMainContact", finalPayload, this.AddRecordSuccess, () => { }, true);
            },
            AddRecordSuccess(response) {
                this.$refs['add-modal'].hide();
                //Perform Global Updates
                this.$GlobalUpdate.ExecuteGlobalUpdate();
            },
            CancelAddRecord() {
                this.$refs['add-modal'].hide();
            },
            //EDIT RECORD
            ShowEditRecord(recordData) {
                this.ResetNewRecord();
                this.NewRecord.Id = recordData.Id;
                this.NewRecord.FirstName = recordData.Volunteer.Person.FirstName;
                this.NewRecord.LastName = recordData.Volunteer.Person.LastName;
                this.NewRecord.PhoneNumber = recordData.Volunteer.Person.PhoneNumbers.length > 0 ? recordData.Volunteer.Person.PhoneNumbers[0].PhoneNumber.FormattedPhoneNumber : "";
                this.NewRecord.Email = recordData.Volunteer.Person.Email;
                this.NewRecord.StartDate = this.formatDate(recordData.StartDate, 'MM/DD/YYYY','');
                this.NewRecord.EndDate = this.formatDate(recordData.EndDate, 'MM/DD/YYYY','');
                this.$refs['edit-modal'].show();

            },
            UpdateRecord(e) {
                e.preventDefault();
                //var vm = this;
                let initialPayload = { ChurchID: this.RecordId };
                let finalPayload = { ...initialPayload, ...this.NewRecord };
                this.$MasterAPI.ExecuteRequest("POST", "ChurchEngagement/People/UpdateChurchContact", finalPayload, this.UpdateRecordSuccess, () => { }, true);

            },
            UpdateRecordSuccess(response) {
                this.$refs['edit-modal'].hide();
                //Perform Global Updates
                this.$GlobalUpdate.ExecuteGlobalUpdate();
            },
            CancelEditRecord() {
                this.$refs['edit-modal'].hide();
            },
            //DELETE RECORD
            ShowDeleteRecord() {
                this.$refs['delete-modal'].show();
            },
            DeleteRecord(e) {
                e.preventDefault();
                //var vm = this;
                let initialPayload = { Id: this.RecordId };
                let finalPayload = { ...initialPayload, ...this.DeleteRecordAdditionalPayload };
                this.$MasterAPI.ExecuteRequest("POST", this.DeleteRecordMethod, finalPayload, this.DeleteRecordSuccess, () => { }, true);

            },
            DeleteRecordSuccess(response) {
                this.$refs['delete-modal'].hide();
                //Perform Global Updates
                this.$GlobalUpdate.ExecuteGlobalUpdate();
            },
            CancelDeleteRecord() {
                this.$refs['delete-modal'].hide();
            },

            CheckPermission(permissionName) {
                return this.$store.getters["User.PrivateProfile.CheckPermission"](permissionName);
            },
            formatDate(value, fmt = 'D MMM YYYY', defaultValue = '') {
                return (value == null)
                    ? defaultValue
                    : moment(value, 'YYYY-MM-DD').format(fmt)
            },
        },
        components: {
            Vuetable,
            flatPickr,
            DeleteIcon
        },
        computed: {
            ModalTitle: function () {
                return "Editing " + this.DataLabel;
            },
            TableState: function () {
                if (this.FieldData.length == 0) {
                    return "NoRecords";
                } else if (this.FieldData.filter(datalist => datalist.EndDate == null).length == 0) {
                    return "AllClosed";
                } else {
                    return "OneOpen";
                }
            }
        }
    }
</script>

<style scoped>
    .horz-icons span {
        margin-left: 10px;
    }

    /deep/ tbody tr.current-record {
        font-weight: bold;
    }

    table {
        table-layout: fixed;
    }
    .vue-datatable {
        width: 100%;
        display: inline-block;
        overflow-x: auto;
    }
</style>